import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbTrail } from 'src/app/data-models/businessConsultation';
import { environment } from 'src/environments/environment';
import { CompanyProfile, GroupPermissions, Profile, WorkOrderCustomField } from '../../data-models/models';
import { PilotModules, PilotModulesSettings } from '../../data-models/pilotModules';
import { NavigatorService } from './navigator.service';

import { CompanyTiers, PlatformService } from './platform.service';

export enum PilotModulesEnum {
    PHONE_CALLS = "calling",
    PROJECTS = "projects",
    DISPATCHING = "dispatching",
    SCHEDULING = "scheduling",
    INVENTORY_MANAGEMENT = "inventory",
    BILLING = "billing",
    LEADS = "leads"
}

@Injectable({
    providedIn: 'root'
})

export class AiCopilotService {

  public isAiChatVisible: boolean = false;
  public isPhoneTrialExpired: boolean = false;
  public isLoading: boolean = false;
  public assignedPhoneNumber: string = "Loading...";

  // Changing this will merge existing pilot module record to keep everything in sync
  private defaultPilotModules: Array<PilotModules> = [
      {
          id: PilotModulesEnum.PHONE_CALLS,
          title: "Phone Calls",
          icon: 'phone',
          isActive: true,
          desc: "Tess acts as your AI receptionist and customer service rep. It can schedule jobs, answer questions, collect customer notes, and route calls to a human only when necessary — making sure no lead is lost and every call is productive.",
          settings: [
            {
              key: "phone", 
              label: "Your assigned phone number", 
              type: "info",
              value: "+1 (123) 345-7890"
            },
            {
              key: "callForwardDesc", 
              label: "To let Tess handle your calls, forward your phone number to the one we've provided. This allows our AI assistant to answer, schedule jobs, and route important calls to you.", 
              type: "info"
            },
            {
              key: "callForwardSubHeader", 
              label: "How to set up call forwarding:", 
              orderedPoints: [
                "Open your Phone app's settings.",

                "Look for Call Forwarding under Call or Network settings.",
                
                "Turn it on and enter the number we've provided.",
                
                "Save your settings."
              ],
              type: "info"
            },
            {
              key: "callForwardOptionalHelp", 
              label: "* Or just simply ask me in the chat window and I can give more specific details", 
              type: "info"
            },
            {
              key: "inboundGreeting", 
              label: "Greeting response to inbound calls", 
              type: "textarea"
            },
            {
              key: "inboundOutroMsg", 
              label: "Outro response at the end to inbound calls", 
              type: "textarea"
            },
            {
              key: "outboundGreeting", 
              label: "Greeting response to outbound calls", 
              type: "textarea"
            },
            {
              key: "groupFilter",
              label: "Choose which groups Tess can assign work orders to",
              type: "multiselect",
              value: ["technicians"],
              options: [ ]
            },
            // { 
            //   key: "recordCustomerNotes", 
            //   label: "Log customer notes into a Project or Ticket after each call", 
            //   type: "toggle" 
            // },
            // { 
            //   key: "callRecording", 
            //   label: "Record all calls for quality and training", 
            //   type: "toggle" 
            // },
            // { 
            //   key: "transcribeCalls", 
            //   label: "Generate transcripts for each call", 
            //   type: "toggle" 
            // },
            { 
              key: "escalationLogic", 
              label: "When should Tess escalate to a human?", 
              type: "dropdown", 
              options: ["Never unless requested", "If caller sounds angry", "If Tess is unsure"] 
            },
            // { 
            //   key: "workingHours", 
            //   label: "Hours Tess should handle calls", 
            //   type: "dropdown", 
            //   options: ["24/7", "Business Hours Only", "Weekends Only"] 
            // },
            // { 
            //   key: "voiceTone", 
            //   label: "Select Tess's speaking style", 
            //   type: "dropdown", 
            //   options: ["Professional", "Friendly", "Casual", "Energetic"] 
            // }
          ]
      },
      // {
      //     id: PilotModulesEnum.PROJECTS,
      //     title: "Projects",
      //     icon: 'construction',
      //     isActive: false,
      //     desc: "Manages end-to-end project workflows, including task tracking, progress updates, and contractor coordination. Tess keeps everything on track and flags delays or bottlenecks.",
      //     settings: [
      //         { 
      //           key: "autoGenerateTasks", 
      //           label: "Auto-generate tasks based on project scope", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "trackProgressFrequency", 
      //           label: "Progress tracking frequency", 
      //           type: "dropdown", 
      //           options: ["Daily", "Weekly", "Bi-weekly", "Monthly"] 
      //         },
      //         { 
      //           key: "notifyDelays", 
      //           label: "Notify team of project delays automatically", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "autoAssignTeam", 
      //           label: "Auto-assign team members based on workload and skill", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "requireApprovalForScopeChanges", 
      //           label: "Require manager approval for scope changes", 
      //           type: "toggle" 
      //         }
      //     ]  
      // },
      {
          id: PilotModulesEnum.DISPATCHING,
          title: "Dispatching",
          icon: 'local_shipping',
          isActive: true,
          desc: "Optimizes technician dispatching in real-time based on location, availability, and job urgency. Tess ensures the right tech is sent to the right job at the right time.",
          settings: [
              { 
                key: "optimizeForSpeedOrEfficiency", 
                label: "Optimize dispatching for", 
                type: "dropdown", 
                options: ["Speed", "Cost Efficiency", "Balanced"] 
              },
              { 
                key: "autoReassignOnDelay", 
                label: "Auto-reassign jobs if a tech is running late", 
                type: "toggle" 
              },
              { 
                key: "geoFencingRange", 
                label: "Geo-fencing radius for dispatch suggestions (miles)", 
                type: "text" 
              },
              { 
                key: "prioritizeSkillMatch", 
                label: "Prioritize skill match over location proximity", 
                type: "toggle" 
              },
              { 
                key: "techAvailabilityBuffer", 
                label: "Add buffer time between tech jobs (minutes)", 
                type: "text" 
              }
          ]              
      },
      {
          id: PilotModulesEnum.SCHEDULING,
          title: "Scheduling",
          icon: 'schedule',
          isActive: true,
          desc: "Automatically schedules jobs, follow-ups, and team availability with smart conflict resolution. Tess adjusts calendars to maximize efficiency and minimize downtime.",
          settings: [
              { 
                key: "autoFillOpenSlots", 
                label: "Automatically fill open time slots with appropriate jobs", 
                type: "toggle" 
              },
              { 
                key: "preventDoubleBooking", 
                label: "Prevent double-booking of technicians", 
                type: "toggle" 
              },
              { 
                key: "smartRescheduling", 
                label: "Auto-reschedule missed or delayed appointments", 
                type: "toggle" 
              },
              { 
                key: "scheduleConfirmationReminder", 
                label: "Send reminders to confirm appointments with clients", 
                type: "toggle" 
              }
          ]  
      },
      // {
      //     id: PilotModulesEnum.INVENTORY_MANAGEMENT,
      //     title: "Inventory Management",
      //     icon: 'warehouse',
      //     isActive: false,
      //     desc: "Tracks parts and supplies across all jobs and warehouses. Tess reorders critical items before you run out and keeps inventory lean and organized.",
      //     settings: [
      //         { 
      //           key: "autoReorderThreshold", 
      //           label: "Auto-reorder parts when stock falls below threshold", 
      //           type: "text" 
      //         },
      //         { 
      //           key: "warnOnLowStock", 
      //           label: "Warn when inventory is low but not yet critical", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "trackUsagePerJob", 
      //           label: "Track parts usage per job for reporting", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "restrictInventoryAccess", 
      //           label: "Restrict part checkout to assigned jobs only", 
      //           type: "toggle" 
      //         }
      //     ]              
      // },
      // {
      //     id: PilotModulesEnum.BILLING,
      //     title: "Billing",
      //     icon: 'request_quote',
      //     isActive: false,
      //     desc: "Handles invoicing, payment tracking, and revenue reporting. Tess makes sure you get paid on time and flags any issues with accounts receivable.",
      //     settings: [
      //         { 
      //           key: "autoInvoiceOnCompletion", 
      //           label: "Send invoice automatically when job is marked complete", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "allowPartialPayments", 
      //           label: "Allow partial payments from clients", 
      //           type: "toggle" 
      //         },
      //         { 
      //           key: "sendPaymentReminders", 
      //           label: "Send payment reminders after X days", 
      //           type: "text" 
      //         },
      //         { 
      //           key: "lateFeeThreshold", 
      //           label: "Apply late fees after X days past due", 
      //           type: "text" 
      //         },
      //         { 
      //           key: "autoReconcilePayments", 
      //           label: "Automatically reconcile payments with bank feed", 
      //           type: "toggle" 
      //         }
      //     ]  
      // },
      {
          id: PilotModulesEnum.LEADS,
          title: "Leads",
          icon: 'emoji_objects',
          isActive: true,
          desc: "Manages incoming leads from the Swarm app and other sources, assigning them to the best-fit techs or teams. Tess boosts conversion rates by following up automatically and tracking results.",
          settings: [
              { 
                key: "autoAssignLeads", 
                label: "Auto-assign leads based on job type and team capacity", 
                type: "toggle" 
              },
              { 
                key: "followUpWindow", 
                label: "Follow up on unclaimed leads after (minutes/hours)", 
                type: "text" 
              },
              { 
                key: "notifyWhenNewLeadAvailable", 
                label: "Notify team when new leads enter the system", 
                type: "toggle" 
              }
          ]
            
      }
      
  ]

  public pilotModules: Array<PilotModules> = [ ];
  public breadcrumbTrail: BreadcrumbTrail = {
    module: undefined,
    subpage: '',
    isNewUser: false
  }

  constructor(
      private http: HttpClient, 
      private navigator: NavigatorService,
      private platformService: PlatformService,
      public router: Router) { 

  }

  public async init() {
    const profile: Profile = this.navigator.getCurrentProfile();
    this.isPhoneTrialExpired = await this.getIsPhoneTrialExpired();

    console.log("Profile: ", profile);
    this.isAiChatVisible = profile?.linked_accounts?.length == 0;

    let companyProfile: CompanyProfile = await this.navigator.getCompanyProfile(profile?.company_id);

    if (companyProfile && (!companyProfile?.assignedPhoneNumbers || companyProfile?.assignedPhoneNumbers?.length == 0)) {
      const phoneNumber: any = await this.purchasePhoneNumber(469);
      companyProfile.assignedPhoneNumbers = [phoneNumber];
    } 

    this.initViewState();
    await this.loadPilotModules();

    try {
      this.setPilotModulePhoneNumber(companyProfile.assignedPhoneNumbers[0]?.prettyPhoneNumber);
      this.setPilotModuleCallGroups();
    } catch {
      // Do something
    }

  }

  private initViewState(): void { }

  private async setPilotModulePhoneNumber(phoneNumber: string | null): Promise<void> {

    const pilotModule: PilotModules = this.pilotModules.find((module: PilotModules) => { return module.id == PilotModulesEnum.PHONE_CALLS });

    if (!pilotModule)
      return;

    const moduleSetting: PilotModulesSettings = pilotModule.settings.find((setting: PilotModulesSettings) => { return setting?.key == "phone"});

    if (!moduleSetting)
      return;

    this.assignedPhoneNumber = phoneNumber;
    moduleSetting.value = phoneNumber;
  } 

  private async setPilotModuleCallGroups(): Promise<void> {

    const pilotModule: PilotModules = this.pilotModules.find((module: PilotModules) => { return module.id == PilotModulesEnum.PHONE_CALLS });

    if (!pilotModule)
      return;

    const moduleSetting: PilotModulesSettings = pilotModule.settings.find((setting: PilotModulesSettings) => { return setting?.key == "groupFilter"});

    if (!moduleSetting)
      return;

    const groups: Array<GroupPermissions> = await this.navigator.getAsyncGroups();
    moduleSetting.options = groups.map(gp => gp.group);
  } 

  private async getIsPhoneTrialExpired(): Promise<boolean> {

    const companyProfile: CompanyProfile = await this.navigator.getAsyncCompanyProfile();
    const minutesUsed: number = await this.platformService.getCallingMinutesUsed(companyProfile?.created_date, new Date());
    const isTrialExpired: boolean = companyProfile?.platformLicense == CompanyTiers.TRIAL && minutesUsed >= 20;

    console.log("Minutes Used: ", minutesUsed);

    return isTrialExpired;
  } 

  public getBreadcrumbTrail(): BreadcrumbTrail {
    return this.breadcrumbTrail;
  }

  public setBreadcrumbTrailModule(module: string): void {
    this.breadcrumbTrail.module = module;
  }

  public setBreadcrumbTrailSubpage(subpage: string): void {
    this.breadcrumbTrail.subpage = subpage;
  }

  public setBreadcrumbTrailIsNewUser(isNewUser: boolean): void {
    this.breadcrumbTrail.isNewUser = isNewUser;
  }

  public async getPilotModules() {
      return this.pilotModules;
  }

  public async setPilotModule(pilotModule) {
      
      let moduleIndex = this.pilotModules.findIndex((module) => { return module?.id == pilotModule?.id });

      if(moduleIndex > -1)
          this.pilotModules[moduleIndex].isActive = pilotModule?.isActive;
  }

  public async togglePilotModule(pilotModule) {
    const profile: Profile = this.navigator.getCurrentProfile();
    
    if (this.isPhoneTrialExpired || !profile?.company_id)
      return;

    let moduleIndex = this.pilotModules.findIndex((module) => { return module?.id == pilotModule?.id });

    if(moduleIndex > -1) 
        this.pilotModules[moduleIndex].isActive = !pilotModule?.isActive;

    this.upsertPilotModules();
  }

  public aiChatToggle() {
    this.isAiChatVisible = this.isAiChatVisible ? false : true;
  }

  private mergePilotModulesWithDefaults(
    loaded: Array<PilotModules>,
    defaults: Array<PilotModules>
  ): Array<PilotModules> {
    return defaults.map(defaultModule => {
      const existingModule = loaded.find(m => m.id === defaultModule.id);
  
      const mergedSettings = defaultModule.settings.map(defaultSetting => {
        const existingSetting = existingModule?.settings?.find(s => s.key === defaultSetting.key);
        return existingSetting
          ? { ...defaultSetting, value: existingSetting.value }
          : { ...defaultSetting };
      });
  
      return {
        id: defaultModule.id,
        title: defaultModule.title,
        icon: defaultModule.icon,
        desc: defaultModule.desc,
        isActive: existingModule?.isActive ?? defaultModule.isActive, // preserve active status if exists
        settings: mergedSettings
      };
    });
  }

  public async purchasePhoneNumber(areaCode: number): Promise<string | null> {
    const token: string | null = this.platformService.getAuthToken();
    let purchasedNumber: string | null = null;
  
    await this.http.post(`${environment.hostURL}/api/calling/purchasePhoneNumber`, { token, areaCode })
      .toPromise()
      .then((data: { message: string, phoneNumber: string | null, success: boolean }) => {
        console.log("Purchased Phone Number", data);
  
        purchasedNumber = data?.phoneNumber ?? null;
      });
  
    return purchasedNumber;
  }

  public async makeIntroCall(): Promise<boolean> {
    const token: string | null = this.platformService.getAuthToken();
    let isSuccess: boolean = false;
  
    await this.http.post(`${environment.hostURL}/api/calling/makeIntroCall`, { token })
      .toPromise()
      .then((data: { message: string, success: boolean }) => {
        console.log("Intro Call: ", data);
  
        isSuccess = data?.success;
      });
  
    return isSuccess;
  }
  
  public async loadPilotModules(): Promise<Array<PilotModules>> {
    this.isLoading = true;
    const token: string | null = this.platformService.getAuthToken();
  
    await this.http.post(`${environment.hostURL}/api/aiPilot/getModules`, { token })
      .toPromise()
      .then((data: { message: string, pilotModules: Array<PilotModules> | null }) => {
        console.log("Pilot Modules", data);
  
        const loadedModules = data?.pilotModules ?? [];
  
        this.pilotModules = this.mergePilotModulesWithDefaults(loadedModules, this.defaultPilotModules);
      });
  
    this.isLoading = false;
    return this.pilotModules;
  }

  public async upsertPilotModules(): Promise<boolean> {

    const profile: Profile = this.navigator.getCurrentProfile();

    if(!profile?.company_id || profile?.company_id?.length == 0)
      return false;

    const token: string | null = this.platformService.getAuthToken();
    let success: boolean = false;

    await this.http.post(`${environment.hostURL}/api/aiPilot/upsertModules`, 
    { token: token, pilotModules: this.pilotModules } ).toPromise().then( 
        (data: { message: string, pilotModules: string | null, success: boolean}
            ) => {
                console.log("Pilot Modules", data );
                success = data?.success
            }
    );

    return success;
  }

}